// Record in Chat: the record control on the meetings page (BUILD 1 WP-4;
// develop2's RecordControl UX, MAP-MEETINGS §13).
//
// Idle: "Record" opens a short panel: what to record (This room / A meeting
// tab / Both; desktop browsers without tab audio offer an OS audio input), the title, the attendees, and the consent line. There is no language
// choice: engines detect the spoken language. "Start recording" is the attestation.
// Recording: a red dot, the input level and the clock beside a filled Stop,
// in one button so a room can see at a glance that it is on and how to end
// it (the composer's ✓ has the same weight). Owner: WP-4. BUILD 2's voice
// notice and per-meeting choice render through meeting-voice.tsx (WP2-4).
import { useEffect, useRef, useState } from 'react';
import { MeetingReminderPreference } from './meeting-reminders';
import { useMeetingCaptureSupport } from './meeting-capture-support';
import { consumeMeetingRecordingRequest, onMeetingRecordingRequest } from './meeting-record-intent';
import { useBilling } from '@/billing/context';
import { useRequestedAppLocale } from '@/i18n/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import audioCopy from '@/i18n/ns/meeting-audio-input';
import audioStatusCopy from '@/i18n/ns/meeting-audio-status';
import { MeetingAudioInput, audioSelectionReady, emptyAudioSelection } from './meeting-audio-input';
import { MeetingAttendeePicker, attendeeInputs } from './meeting-attendees';
import { formatClock, LevelMeter } from './meeting-record-parts';
import { meetingRecorderDeps } from './meeting-recorder-deps';
import { installBrowserMeetingRecorder } from './meeting-recorder-runtime';
import { meetingRecorder, recorderElapsedMs, useMeetingRecorder } from './meeting-recorder';
import { WorkspaceIcon } from './icons';
import { MeetingDialog, MeetingDialogTitle } from './meeting-dialog';
import { RecordVoiceNotice } from './meeting-voice';
import {
  readMeetingAudioPreferences,
  rememberMeetingAudioPreferences,
} from './meeting-audio-preferences';
import type { CompanyMember, MeetingCapabilities } from './types';

const SOURCE_CHOICES = ['room', 'tab', 'both'] as const;
const SOURCE_COPY = {
  room: ['sourceRoom', 'sourceRoomHint'],
  tab: ['sourceTab', 'sourceTabHint'],
  both: ['sourceBoth', 'sourceBothHint'],
} as const;

/** Tab capture needs audio support, not merely screen sharing support. */
export function tabRecordingSupported(): boolean {
  const deps = meetingRecorderDeps();
  return Boolean(deps && deps.captureSupport().tab && !deps.isMobile());
}

function defaultTitle(template: string, locale: string, now: Date) {
  let date: string;
  try {
    date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(now);
  } catch {
    date = now.toISOString().slice(0, 16).replace('T', ' ');
  }
  return formatCopy(template, { date });
}

function RecordPanel({
  members,
  currentUserId,
  companyName,
  capabilities,
  desktopSystem,
  onClose,
}: {
  members: CompanyMember[];
  currentUserId: string | undefined;
  companyName: string;
  capabilities: MeetingCapabilities | null;
  desktopSystem: boolean;
  onClose: () => void;
}) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const inputCopy = useNamespaceCopy(audioCopy);
  const statusCopy = useNamespaceCopy(audioStatusCopy);
  const locale = useRequestedAppLocale();
  const tabs = tabRecordingSupported();
  const remembered = readMeetingAudioPreferences();
  const rememberedSource = remembered?.source;
  const sourceTouched = useRef(Boolean(rememberedSource));
  const [source, setSource] = useState<'room' | 'tab' | 'both'>(
    rememberedSource ?? (desktopSystem ? 'both' : 'room'),
  );
  useEffect(() => {
    if (desktopSystem && !rememberedSource && !sourceTouched.current) setSource('both');
  }, [desktopSystem, rememberedSource]);
  const [audioSelection, setAudioSelection] = useState(emptyAudioSelection);
  const desktop = Boolean(meetingRecorderDeps() && !meetingRecorderDeps()!.isMobile());
  const system = desktop && source !== 'room' && (desktopSystem || !tabs);
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<string[]>(() =>
    currentUserId && members.some((member) => member.id === currentUserId) ? [currentUserId] : [],
  );
  const [guests, setGuests] = useState('');
  const [voiceIdentification, setVoiceIdentification] = useState(true);
  const placeholder = defaultTitle(copy.defaultTitle, locale, new Date());
  const start = () => {
    const typed = title.trim();
    const identification = voiceIdentification;
    onClose();
    void meetingRecorder
      .start(currentUserId ?? '', {
        title: typed || placeholder,
        titleIsDefault: !typed,
        source: system ? (source === 'both' ? 'system-both' : 'system') : source,
        ...(system && !desktopSystem ? audioSelection : {}),
        participants: attendeeInputs(selected, guests),
        voiceIdentification: identification,
      })
      .then(() => {
        if (meetingRecorder.store.getState().phase === 'recording')
          rememberMeetingAudioPreferences({ source, selection: audioSelection });
      });
  };
  return (
    <MeetingDialog
      label={copy.recordDialogTitle}
      className="ws-record-dialog"
      keepOnOutsidePress
      onClose={onClose}
    >
      <header>
        <div>
          <MeetingDialogTitle>{copy.recordDialogTitle}</MeetingDialogTitle>
          <p>{copy.recordDialogNote}</p>
        </div>
        <button className="ws-icon-button" aria-label={copy.close} onClick={onClose}>
          <WorkspaceIcon name="close" />
        </button>
      </header>
      <div className="ws-meeting-dialog-body">
        <fieldset className="ws-record-sources">
          <legend>{copy.sourceLabel}</legend>
          {SOURCE_CHOICES.filter((choice) => choice === 'room' || desktop).map((choice) => (
            <label key={choice} className="ws-record-source">
              <input
                type="radio"
                name="meeting-record-source"
                value={choice}
                checked={source === choice}
                onClick={() => {
                  sourceTouched.current = true;
                }}
                onChange={() => setSource(choice)}
              />
              <span>
                {desktopSystem && choice !== 'room'
                  ? choice === 'both'
                    ? `${statusCopy.desktopSource} + ${statusCopy.microphoneLevel}`
                    : statusCopy.desktopSource
                  : choice === 'tab' && !tabs
                    ? inputCopy.source
                    : copy[SOURCE_COPY[choice][0]]}
                <small>
                  {desktopSystem && choice !== 'room'
                    ? statusCopy.desktopSourceHint
                    : choice !== 'room' && !tabs
                      ? choice === 'both'
                        ? inputCopy.bothHint
                        : inputCopy.hint
                      : copy[SOURCE_COPY[choice][1]]}
                </small>
              </span>
            </label>
          ))}
          {source !== 'room' && tabs && !desktopSystem && (
            <p className="ws-page-note">{copy.tabPickerNote}</p>
          )}
          {capabilities && (
            <p className="ws-meeting-audio-preflight" data-recording-preflight>
              {capabilities.preview.available ? copy.previewCaption : copy.previewUnavailable}
            </p>
          )}
          {system && !desktopSystem && (
            <MeetingAudioInput
              both={source === 'both'}
              value={audioSelection}
              onChange={setAudioSelection}
            />
          )}
        </fieldset>
        <label>
          {copy.titleLabel}
          <input
            value={title}
            placeholder={placeholder}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <MeetingAttendeePicker
          members={members}
          currentUserId={currentUserId}
          companyName={companyName}
          selected={selected}
          onSelected={setSelected}
          guests={guests}
          onGuests={setGuests}
        />
        <p className="ws-record-consent">{copy.consentLine}</p>
        <RecordVoiceNotice onPendingChange={setVoiceIdentification} />
        <MeetingReminderPreference />
      </div>
      <footer>
        <button className="ws-button" onClick={onClose}>
          {copy.cancel}
        </button>
        <button
          className="ws-button primary"
          disabled={
            !capabilities?.record.available ||
            (system && !desktopSystem && !audioSelectionReady(audioSelection, source === 'both'))
          }
          onClick={start}
        >
          <span className="ws-record-dot" aria-hidden="true" /> {copy.startRecording}
        </button>
      </footer>
    </MeetingDialog>
  );
}

/** The live button while this device records: dot, level and clock, then a filled Stop. */
export function RecordingButton({ compact = false }: { compact?: boolean }) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const phase = useMeetingRecorder((state) => state.phase);
  const level = useMeetingRecorder((state) => state.level);
  const elapsed = useMeetingRecorder(recorderElapsedMs);
  const clock = formatClock(elapsed);
  return (
    <button
      className={`ws-button ws-recording-button${compact ? ' compact' : ''}`}
      onClick={() => void meetingRecorder.stop()}
      disabled={phase !== 'recording'}
      // A stable name: the clock and the level change every moment and are
      // not re-announced to a screen reader for the whole meeting.
      aria-label={copy.stop}
    >
      <span className="ws-record-dot live" aria-hidden="true" />
      <span aria-hidden="true">
        <LevelMeter level={level} label={copy.inputLevel} />
      </span>
      <span className="ws-record-clock" aria-hidden="true">
        {clock}
      </span>
      <span className="ws-recording-stop" aria-hidden="true">
        {copy.stop}
      </span>
    </button>
  );
}

/**
 * The recorder's error; a stop the server refused offers Retry (sign in again
 * first). A start refused for used-up usage credit opens billing, as a chat
 * question does (#2841).
 */
export function RecorderError() {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const statusCopy = useNamespaceCopy(audioStatusCopy);
  const error = useMeetingRecorder((state) => state.error);
  const billingState = useBilling();
  const billing = useRef(billingState);
  billing.current = billingState;
  // Once per refusal: the billing state object changes on every refresh.
  useEffect(() => {
    if (error === 'paymentRequired') billing.current?.request();
  }, [error]);
  if (!error) return null;
  const message =
    error === 'nativeDenied'
      ? statusCopy.nativeDenied
      : error === 'nativeUnavailable'
        ? statusCopy.nativeUnavailable
        : copy[error];
  return (
    <div className="ws-record-error" role="alert">
      <span>{message}</span>
      {error === 'stopFailed' && (
        <button className="ws-button" onClick={() => void meetingRecorder.retryStop()}>
          {copy.retryStop}
        </button>
      )}
      <button className="ws-text-button" onClick={() => meetingRecorder.dismissError()}>
        {copy.dismiss}
      </button>
    </div>
  );
}

export function MeetingRecordControl({
  members,
  currentUserId,
  companyName,
  onRecording,
}: {
  members: CompanyMember[];
  currentUserId: string | undefined;
  companyName: string;
  /** The meeting this device just started recording (open its canvas). */
  onRecording: (meetingId: string) => void;
}) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const phase = useMeetingRecorder((state) => state.phase);
  const meetingId = useMeetingRecorder((state) => state.meetingId);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const consume = () => {
      if (consumeMeetingRecordingRequest()) setOpen(true);
    };
    consume();
    return onMeetingRecordingRequest(consume);
  }, []);
  const [capabilities, setCapabilities] = useState<MeetingCapabilities | null>(null);
  const support = useMeetingCaptureSupport();
  useEffect(() => {
    let active = true;
    installBrowserMeetingRecorder();
    void meetingRecorder.prepare().then((value) => {
      if (active) setCapabilities(value);
    });
    return () => {
      active = false;
    };
  }, []);
  // Only the start this control made opens the meeting, not a return to the page.
  const previous = useRef(phase);
  useEffect(() => {
    if (previous.current === 'starting' && phase === 'recording' && meetingId) {
      onRecording(meetingId);
    }
    previous.current = phase;
  }, [phase, meetingId]);
  if (phase === 'recording' || phase === 'stopping') return <RecordingButton />;
  const available = Boolean(meetingRecorderDeps() && capabilities?.record?.available);
  const desktopSystem = Boolean(support?.desktopSystem && !meetingRecorderDeps()?.isMobile());
  const busy =
    phase === 'starting' || phase === 'saving' || phase === 'recovering' || phase === 'interrupted';
  return (
    <>
      <button
        className="ws-button primary ws-record-button"
        disabled={!available || busy}
        title={available ? undefined : copy.recordUnavailable}
        onClick={() => setOpen(true)}
      >
        <span className="ws-record-dot" aria-hidden="true" />{' '}
        {phase === 'starting' ? copy.starting : copy.record}
      </button>
      {open && (
        <RecordPanel
          members={members}
          currentUserId={currentUserId}
          companyName={companyName}
          capabilities={capabilities}
          desktopSystem={desktopSystem}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
